import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Zap, CheckCircle2, Clock3 } from "lucide-react";
import client, { formatErr } from "../lib/api";
import { toast } from "sonner";

function timeLeft(expires) {
    const ms = new Date(expires).getTime() - Date.now();
    if (ms <= 0) return "Expired";
    const totalSec = Math.floor(ms / 1000);
    if (totalSec < 60) return `${totalSec}s`;
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

function intensityLeft(deadline) {
    const ms = new Date(deadline).getTime() - Date.now();
    if (ms <= 0) return null;
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}:${String(sec).padStart(2, "0")}` : `${sec}s`;
}

export default function ActiveQuests({ onChange }) {
    const [list, setList] = useState([]);
    const [busy, setBusy] = useState(null);
    const [, force] = useState(0);

    const load = () => client.get("/quests/active").then((r) => setList(r.data));
    useEffect(() => {
        load();
        const t = setInterval(() => force((v) => v + 1), 1000);
        return () => clearInterval(t);
    }, []);

    const startQuest = async (q) => {
        setBusy(q.id);
        try {
            await client.post(`/quests/${q.id}/start`);
            toast.success("Clock started — go.");
            await load();
        } catch (e) {
            toast.error(formatErr(e));
        } finally {
            setBusy(null);
        }
    };

    const complete = async (q) => {
        setBusy(q.id);
        try {
            const { data } = await client.post(`/quests/${q.id}/complete`);
            if (data.already_completed) {
                toast.info("Already locked in for this quest.");
            } else {
                toast.success(`Quest crushed. +${data.xp_awarded} XP.`);
            }
            await load();
            if (onChange) onChange();
        } catch (e) {
            toast.error(formatErr(e));
        } finally {
            setBusy(null);
        }
    };

    if (!list.length) return null;

    return (
        <div className="mt-4 space-y-3" data-testid="active-quests">
            {list.map((q) => {
                const Icon = Icons[q.icon] || Zap;
                const done = q.completed;
                const left = timeLeft(q.expires_at);
                const expired = left === "Expired";
                return (
                    <motion.div
                        key={q.id}
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        data-testid={`quest-card-${q.id}`}
                        className="relative overflow-hidden p-4 border-2"
                        style={{
                            background: done
                                ? "linear-gradient(135deg, #062006 0%, #0d0d0d 70%)"
                                : "linear-gradient(135deg, #1a0a06 0%, #0d0d0d 65%)",
                            borderColor: done ? "#39FF14" : expired ? "#444" : "#FF3B30",
                            boxShadow: done
                                ? "0 0 18px rgba(57,255,20,0.18)"
                                : expired
                                  ? "none"
                                  : "0 0 28px rgba(255,59,48,0.28)",
                        }}
                    >
                        <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full blur-3xl"
                             style={{ background: done ? "#39FF1430" : "#FF3B3030" }} />
                        <div className="relative flex items-center gap-3">
                            <div
                                className="w-12 h-12 flex items-center justify-center shrink-0"
                                style={{
                                    background: done ? "#39FF1420" : "#FF3B3020",
                                    border: `1px solid ${done ? "#39FF14" : "#FF3B30"}`,
                                }}
                            >
                                {done ? (
                                    <CheckCircle2 size={20} className="text-neon-green" />
                                ) : (
                                    <Icon size={20} className="text-neon-orange" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="label-cap text-[10px]"
                                          style={{ color: done ? "#39FF14" : "#FF3B30" }}>
                                        {done ? "Quest Locked" : "Quest · Live"}
                                    </span>
                                    {!done && (
                                        <span className="flex items-center gap-1 text-[10px] label-cap text-zinc-400">
                                            <Clock3 size={10} /> {left}
                                        </span>
                                    )}
                                </div>
                                <div className="font-display font-black text-lg uppercase tracking-tight leading-tight">
                                    {q.title}
                                </div>
                                <div className="text-xs text-zinc-400 mt-1 line-clamp-2">{q.description}</div>
                                <div className="text-[11px] text-neon-green font-bold uppercase tracking-wider mt-1">
                                    +{q.xp_reward} XP
                                </div>
                            </div>
                        </div>
                        {!done && !expired && (() => {
                            const needsStart = !!q.duration_seconds;
                            const started = !!q.started_at;
                            const intensity = q.deadline_at ? intensityLeft(q.deadline_at) : null;
                            const intensityExpired = needsStart && started && !intensity;
                            if (needsStart && !started) {
                                return (
                                    <button
                                        onClick={() => startQuest(q)}
                                        disabled={busy === q.id}
                                        data-testid={`quest-start-${q.id}`}
                                        className="btn-neon w-full py-2 mt-3 text-sm disabled:opacity-50"
                                    >
                                        {busy === q.id ? "Starting..." : `Start (${Math.floor(q.duration_seconds / 60) || 0}m ${q.duration_seconds % 60}s window)`}
                                    </button>
                                );
                            }
                            if (intensityExpired) {
                                return (
                                    <div
                                        data-testid={`quest-intensity-expired-${q.id}`}
                                        className="mt-3 surface-3 py-2 text-center label-cap text-[11px] text-zinc-500"
                                    >
                                        Intensity window expired
                                    </div>
                                );
                            }
                            return (
                                <>
                                    {needsStart && intensity && (
                                        <div
                                            className="mt-3 flex items-center justify-between bg-neon-orange/10 border border-neon-orange px-3 py-1.5"
                                            data-testid={`quest-intensity-${q.id}`}
                                        >
                                            <span className="label-cap text-[10px] text-neon-orange">
                                                Intensity
                                            </span>
                                            <span className="font-display font-black tabular-nums text-neon-orange text-lg leading-none">
                                                {intensity}
                                            </span>
                                        </div>
                                    )}
                                    <button
                                        onClick={() => complete(q)}
                                        disabled={busy === q.id}
                                        data-testid={`quest-complete-${q.id}`}
                                        className="btn-primary w-full py-2 mt-3 text-sm disabled:opacity-50"
                                    >
                                        {busy === q.id ? "Locking..." : "I Crushed It"}
                                    </button>
                                </>
                            );
                        })()}
                        {done && (
                            <div className="mt-3 surface-3 py-2 text-center label-cap text-[11px] text-neon-green flex items-center justify-center gap-1">
                                <CheckCircle2 size={11} /> Locked in
                            </div>
                        )}
                    </motion.div>
                );
            })}
        </div>
    );
}
